import React, { useState, FC } from 'react';
import { Container, Grid, Box, IconButton, Select, MenuItem, Hidden, Tabs, Tab, Button, Divider, Typography } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import clsx from 'clsx';

import { routes } from 'router/routesList';
import { intl } from 'helpers';
import { useNewsFeed } from 'hooks/feed';
import { DesertImage } from 'assets/svg-js';
import { LIST_NEWS_FEED } from 'services/graphql/query/feed';
import type { PostFeed, ActivityPost, OpportunityFeed } from 'types/post';
import { NewsFeedCollectionEnum } from 'types/post';

import { Loader } from 'components/Loader';
import { InfoPost } from 'components/InfoPost';
import { OpportunityPost } from 'components/OpportunityPost';

import { useStyles } from './styles';
import { TabPanel, a11yProps } from './components/TabPanel';

const LIMIT = 10;

const NewsFeed: FC = () => {
  const { tab } = useParams();
  const styles = useStyles();
  const [currentTab, setCurrentTab] = useState<NewsFeedCollectionEnum>(tab ?? NewsFeedCollectionEnum.general);
  const { handleSuccessDelete, handleSuccessLike, handleSuccessShare } = useNewsFeed({ collection: currentTab, limit: LIMIT });

  const allTabRoute = `${routes.ADMIN_NEWS_FEED}/${NewsFeedCollectionEnum.general}`;
  const myActivityTabRoute = `${routes.ADMIN_NEWS_FEED}/${NewsFeedCollectionEnum.my_activity_feed}`;

  const { data, loading, refetch, fetchMore } = useQuery<any>(LIST_NEWS_FEED, {
    variables: {
      filter: {
        collection: currentTab,
      },
      pagination: {
        limit: LIMIT,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const handleTabChange = (_: any, newValue: NewsFeedCollectionEnum) => {
    refetch({ filter: { collection: newValue } });
    setCurrentTab(newValue);
  };

  const onShowMore = () => {
    fetchMore({
      variables: {
        filter: {
          collection: currentTab,
        },
        pagination: {
          offset: data?.listNewsFeed?.items?.length,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        if (fetchMoreResult?.listNewsFeed?.meta?.total < LIMIT) return prev;
        return {
          ...prev,
          listNewsFeed: {
            ...prev.listNewsFeed,
            items: [...prev.listNewsFeed.items, ...fetchMoreResult?.listNewsFeed?.items],
          },
        };
      },
    });
  };

  return (
    <Container className={styles.wrapper}>
      <Grid container>
        <Grid item lg={12} xs={12}>
          <Box className={styles.heading}>
            <Hidden only="xs">
              <Tabs value={currentTab} indicatorColor="primary" onChange={handleTabChange}>
                <Tab
                  className={styles.tab}
                  value={NewsFeedCollectionEnum.general}
                  component={Link}
                  to={allTabRoute}
                  label={intl('NEWS_FEED.allNews')}
                  {...a11yProps(0)}
                />
                <Tab
                  className={styles.tab}
                  value={NewsFeedCollectionEnum.my_activity_feed}
                  component={Link}
                  to={myActivityTabRoute}
                  label={intl('NEWS_FEED.myActivityFeed')}
                  {...a11yProps(1)}
                />
              </Tabs>
              <Button className={styles.btnAdd} variant="text" color="primary" startIcon={<AddIcon />} component={Link} to={routes.ADMIN_NEW_POST}>
                {intl('NEWS_FEED.newPost')}
              </Button>
            </Hidden>
            <Hidden smUp>
              <Select
                labelId="news-tab-switcher-label"
                id="news-tab-switcher"
                className={styles.select}
                value={tab}
                onChange={(e) => handleTabChange(null, e.target.value as NewsFeedCollectionEnum)}
              >
                <MenuItem value={NewsFeedCollectionEnum.general}>
                  <Link className={styles.mobileLink} to={NewsFeedCollectionEnum.general}>
                    {intl('NEWS_FEED.allNews')}
                  </Link>
                </MenuItem>
                <MenuItem value={NewsFeedCollectionEnum.my_activity_feed}>
                  <Link className={styles.mobileLink} to={NewsFeedCollectionEnum.my_activity_feed}>
                    {intl('NEWS_FEED.myActivityFeed')}
                  </Link>
                </MenuItem>
              </Select>
              <IconButton className={styles.btnAdd} component={Link} to={routes.ADMIN_NEW_POST}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Hidden>
          </Box>
          <Divider className={styles.divider} />
          {loading ? (
            <Loader className={styles.loader} />
          ) : data?.listNewsFeed?.meta?.total ? (
            <Grid item lg={9}>
              <TabPanel value={currentTab} name={NewsFeedCollectionEnum.general}>
                {data?.listNewsFeed?.items?.map((n: ActivityPost) =>
                  n?.__typename === 'PostFeed' ? (
                    <InfoPost
                      userRole="admin"
                      post={n as PostFeed}
                      key={n.id}
                      onSuccessLike={handleSuccessLike}
                      onSuccessDelete={handleSuccessDelete}
                      onSuccessShare={handleSuccessShare}
                    />
                  ) : (
                    <OpportunityPost userRole="admin" opportunity={n as OpportunityFeed} key={n.id} onSuccessLike={handleSuccessLike} />
                  ),
                )}
                {data?.listNewsFeed?.meta?.total > data?.listNewsFeed?.items?.length && (
                  <Box className={styles.showMoreContainer}>
                    <Button className={styles.buttonShowMore} variant="outlined" onClick={onShowMore} tabIndex={NaN} component="div" role="none">
                      {intl('SHARED.showMore')}
                    </Button>
                  </Box>
                )}
              </TabPanel>
              <TabPanel value={currentTab} name={NewsFeedCollectionEnum.my_activity_feed}>
                {data?.listNewsFeed?.items?.map((n: ActivityPost) =>
                  n?.__typename === 'PostFeed' ? (
                    <InfoPost
                      userRole="admin"
                      post={n as PostFeed}
                      key={n.id}
                      onSuccessLike={handleSuccessLike}
                      onSuccessDelete={handleSuccessDelete}
                      onSuccessShare={handleSuccessShare}
                    />
                  ) : (
                    <OpportunityPost userRole="admin" opportunity={n as OpportunityFeed} key={n.id} onSuccessLike={handleSuccessLike} />
                  ),
                )}
                {data?.listNewsFeed?.meta?.total > data?.listNewsFeed?.items?.length && (
                  <Box className={styles.showMoreContainer}>
                    <Button className={styles.buttonShowMore} variant="outlined" onClick={onShowMore} tabIndex={NaN} component="div" role="none">
                      {intl('SHARED.showMore')}
                    </Button>
                  </Box>
                )}
              </TabPanel>
            </Grid>
          ) : (
            <Box className={styles.emptyBox}>
              <DesertImage className={styles.emptyIcon} />
              <Typography className={clsx(styles.title, styles.emptyText)}>
                {currentTab === NewsFeedCollectionEnum.my_activity_feed ? intl('EMPTY_STATE.noYourPosts') : intl('EMPTY_STATE.noPosts')}
              </Typography>
              {currentTab === NewsFeedCollectionEnum.my_activity_feed && (
                <Button className={styles.btnCreate} variant="contained" color="primary" component={Link} to={routes.ADMIN_NEW_POST}>
                  {intl('BRAND.ACTIVITY_FEED.createPost')}
                </Button>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export { NewsFeed };
